<template>
	<UserPageWrapper :accountSettings="true">
		<Title>{{ $t("seo.pages.linkedin.title") }}</Title>
		<Meta name="description" :content="$t('seo.pages.linkedin.description')" />
		<Link rel="canonical" :href="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:url" :content="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:type" content="website" />
		<Meta
			property="og:locale"
			:content="`${$i18n.locale}_${$i18n.locale == 'en' ? 'US' : $i18n.locale.toUpperCase()}`"
		/>
		<Meta property="og:title" :content="$t('seo.pages.linkedin.title')" />
		<Meta property="og:description" :content="$t('seo.pages.linkedin.description')" />
		<Meta
			property="og:image"
			content="https://res.cloudinary.com/djbs/image/upload/v1669288084/content/DreamJobs/Graphics/ogimage_dreamjobs.png"
		/>
		<Meta property="og:image:type" content="image/jpeg" />
		<Meta property="og:image:width" content="1200" />
		<Meta property="og:image:height" content="628" />

		<DjBox class="bg-gray-100 md:bg-white border-0 md:border pt-5 md:pt-8 px-5 md:px-10 pb-5 md:pb-10 md:mb-4">
			<BaseHeading type="h2" class="leading-3 text-center md:text-left mb-6">
				<span class="text-2xl">{{ $t("user.linkedin.title") }}</span>
			</BaseHeading>

			<DjBox
				class="flex items-center justify-between bg-gray-100 md:bg-white border-0 md:border pt-0 md:pt-8 pl-0 md:pl-8 pr-0 md:pr-8 pb-0 md:pb-3"
			>
				<div class="hidden md:flex items-center w-[30%] pb-5 sm:pb-3">
					<BaseCloudImage
						class="mr-4"
						public-id="content/DreamJobs/Graphics/icon_linkedin"
						:transformation="[{ width: 28, height: 28, radius: 0, crop: 'thumb' }]"
						postfix=".svg"
						alt="Registration LinkedIn"
					/>
					Linkedin
				</div>
				<BaseTextbox
					v-model="linkedinUrl"
					@keydown="clearErrors()"
					name="firstName"
					:label="'LinkedIn URL'"
					:placeholder="'LinkedIn URL'"
					:error="linkedinUrlError"
					class="md:max-w-[70%]"
					width="100%"
				/>
			</DjBox>

			<div class="flex justify-end">
				<BaseButton @click="save" class="min-w-full sm:min-w-min px-12">{{ $t("user.save") }}</BaseButton>
			</div>
		</DjBox>
	</UserPageWrapper>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import { validateLinkedInUrl } from "~~/utils";

export default defineComponent({
	name: "UserLinkedInPage",
	setup(_props) {
		definePageMeta({
			middleware: ["auth"],
		});

		const { locale, t } = useI18n();
		const app = useNuxtApp();
		const isAuth = useState("isAuth");
		const isMounted = ref(false);

		const region = ref("HU");
		const linkedinUrl = ref("");
		const linkedinUrlError = ref("");

		const serverFieldErrors = ref({});
		const serverErrorMsg = ref("");

		function clearErrors() {
			linkedinUrlError.value.length > 0 ? (linkedinUrlError.value = "") : null;

			Object.keys(serverFieldErrors.value).length ? (serverFieldErrors.value = {}) : null;
			serverErrorMsg.value.length > 0 ? (serverErrorMsg.value = "") : null;
		}

		function save() {
			const data = {
				linkedinurl: linkedinUrl.value,
			};

			if (validateLinkedInUrl(linkedinUrl.value) || linkedinUrl.value.length < 1) {
				$fetch(`/api/update-profile`, {
					// API for all profile update
					method: "PUT",
					body: {
						...data,
						xsrf: useAuth.getXsrfToken(),
						token: useCookie("token").value,
						operation: "update-linkedin-url",
						locale: locale.value,
						region: region.value,
					},
				})
					.then((_response) => {
						updateUser();
						try {
							app.$openToast({ message: t("user.successfulSave"), type: "success", description: "" });
						} catch (error) {
							console.error(error);
						}
					})
					.catch((error) => {
						if (error.response && error.response._data) {
							serverFieldErrors.value = error.response._data.data.errors || {};
						}
						if (error.response && error.response._data && error.response._data.message) {
							serverErrorMsg.value = error.statusText || error.response._data.message || "";
						}
					});
			} else {
				linkedinUrlError.value = t("job.modal.linkedInUrlError");
			}
		}

		function updateUser() {
			useAuth.updateUser().then(() => {
				getLinkedinData();
			});
		}

		function getLinkedinData() {
			const userData = useAuth.getUser();

			linkedinUrl.value = userData.linkedinurl;
		}

		watch(isAuth, (val: boolean) => {
			val == true && getLinkedinData();
		});

		onMounted(() => {
			region.value = useRegion.getCurrentRegion().code;
			isAuth.value == true && getLinkedinData();
			isMounted.value = true;
		});

		return {
			region,
			linkedinUrl,
			linkedinUrlError,
			serverErrorMsg,
			serverFieldErrors,
			clearErrors,
			save,
		};
	},
});
</script>
